<script setup lang="ts">
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import SimpleTextTooltip from '@/common/components/molecules/simpleTextTooltip/SimpleTextTooltip.vue';
import DashboardElement from './DashboardElement.vue';
import { BASE_MENU_VALUE } from '../baseMenu.define';
import { setup } from './rightPanelMenu.setup';
import RightPanelSubMenuElement from './RightPanelSubMenuElement.vue';

const {
  t,

  panelMenuTitle,
  tooltipMouseEvent,
  subPanelInfo,

  shouldMidMenuTitleRendered,
  separatedMenuItems,
  separatedDashboardItems,
  isEmptyDashboardItems,

  onMouseEventInTarget,
  onClosePanel,
  onClickGoToDashboardList,
} = setup();
</script>

<template>
  <transition name="panel-slide-in">
    <nav
      v-if="subPanelInfo.openedSubPanelMenuInfo"
      class="panel-menu"
    >
      <div class="base-menu-header panel-menu-header">
        <div class="panel-menu-header__row">
          <div class="panel-menu-header__left">
            <h2>{{ panelMenuTitle }}</h2>
          </div>
          <base-button
            tag="button"
            class="base-menu-header__slide-button"
            @click="onClosePanel"
            @mouseenter="onMouseEventInTarget"
            @mouseleave="onMouseEventInTarget"
          >
            <ev-icon icon="icon-left-panel" />
          </base-button>
        </div>
        <simple-text-tooltip
          :mouse-event="tooltipMouseEvent"
          base-pos="bottom"
          :text="`${t('WORD.HIDE_MENU')}`"
        />
      </div>
      <ul class="panel-menu__list">
        <template v-if="subPanelInfo.openedSubPanelMenuInfo.value === BASE_MENU_VALUE.DASHBOARD">
          <!-- 빈 상태 -->
          <template v-if="isEmptyDashboardItems">
            <li class="dashboard-item-empty__state">
              <div class="dashboard-item-empty__message">
                {{ t('MESSAGE.NO_REGISTERED_DASHBOARD') }}
              </div>
              <base-button
                tag="button"
                class="dashboard-item-empty__button"
                @click="onClickGoToDashboardList"
              >
                {{ t('MESSAGE.GO_TO_DASHBOARD_LIST') }}
                <ev-icon icon="ev-icon-arrow-right2" />
              </base-button>
            </li>
          </template>

          <!-- 즐겨찾기 영역 -->
          <template v-else-if="separatedDashboardItems.favoriteItems.length > 0">
            <li class="mid-menu">
              <div class="mid-menu__title">
                <span>{{ t('WORD.FAVORITE') }}</span>
              </div>
            </li>
            <dashboard-element
              v-for="(info, idx) in separatedDashboardItems.favoriteItems"
              :key="`favorite_${info.text}__${idx}`"
              :selected="subPanelInfo.selectedSubPanelMenu === info.value"
              :dashboard-info="info"
            />
          </template>

          <!-- 하단 항목 영역 -->
          <template v-if="separatedDashboardItems.regularItems.length > 0">
            <li class="mid-menu">
              <div class="mid-menu__title">
                <span>{{ t('WORD.ITEMS') }}</span>
              </div>
            </li>
            <dashboard-element
              v-for="(info, idx) in separatedDashboardItems.regularItems"
              :key="`regular_${info.text}__${idx}`"
              :selected="subPanelInfo.selectedSubPanelMenu === info.value"
              :dashboard-info="info"
            />
          </template>
        </template>
        <template
          v-else-if="subPanelInfo.openedSubPanelMenuInfo.value === BASE_MENU_VALUE.ANALYSISBOARD"
        >
          <!-- 빈 상태 -->
          <template v-if="isEmptyDashboardItems">
            <li class="dashboard-item-empty__state">
              <div class="dashboard-item-empty__message">
                {{ t('MESSAGE.NO_REGISTERED_DASHBOARD') }}
              </div>
              <base-button
                tag="button"
                class="dashboard-item-empty__button"
                @click="onClickGoToDashboardList"
              >
                {{ t('MESSAGE.GO_TO_DASHBOARD_LIST') }}
                <ev-icon icon="ev-icon-arrow-right2" />
              </base-button>
            </li>
          </template>

          <!-- 즐겨찾기 영역 -->
          <template v-else-if="separatedDashboardItems.favoriteItems.length > 0">
            <li class="mid-menu">
              <div class="mid-menu__title">
                <span>{{ t('WORD.FAVORITE') }}</span>
              </div>
            </li>
            <dashboard-element
              v-for="(info, idx) in separatedDashboardItems.favoriteItems"
              :key="`favorite_${info.text}__${idx}`"
              :selected="subPanelInfo.selectedSubPanelMenu === info.value"
              :dashboard-info="info"
            />
          </template>

          <!-- 하단 항목 영역 -->
          <template v-if="separatedDashboardItems.regularItems.length > 0">
            <li class="mid-menu">
              <div class="mid-menu__title">
                <span>{{ t('WORD.ITEMS') }}</span>
              </div>
            </li>
            <dashboard-element
              v-for="(info, idx) in separatedDashboardItems.regularItems"
              :key="`regular_${info.text}__${idx}`"
              :selected="subPanelInfo.selectedSubPanelMenu === info.value"
              :dashboard-info="info"
            />
          </template>
        </template>
        <template v-else>
          <!-- 즐겨찾기 영역 -->
          <template v-if="separatedMenuItems.favoriteItems.length > 0">
            <li class="mid-menu">
              <div class="mid-menu__title">
                <span>{{ t('WORD.FAVORITE') }}</span>
              </div>
            </li>
            <template
              v-for="(info, idx) in separatedMenuItems.favoriteItems"
              :key="`favorite_${info.text}__${idx}`"
            >
              <right-panel-sub-menu-element
                v-if="!info.children?.length"
                :menu-info="info"
                :selected="subPanelInfo.selectedSubPanelMenu === info.value"
              />

              <li
                v-else
                class="mid-menu"
              >
                <div
                  v-if="shouldMidMenuTitleRendered(info.text)"
                  :class="['mid-menu__title', { 'mid-menu__title--empty': !info.text }]"
                >
                  <span>{{ info.text }}</span>
                </div>
                <ul>
                  <template
                    v-for="(childMenu, childIndex) in info.children"
                    :key="`${childMenu.value}_${childIndex}`"
                  >
                    <right-panel-sub-menu-element
                      :menu-info="childMenu"
                      :selected="subPanelInfo.selectedSubPanelMenu === childMenu.value"
                    />
                  </template>
                </ul>
              </li>
            </template>
          </template>

          <!-- 하단 항목 영역 -->
          <template v-if="separatedMenuItems.regularItems.length > 0">
            <li
              v-if="separatedMenuItems.favoriteItems.length > 0"
              class="mid-menu"
            >
              <div class="mid-menu__title">
                <span>{{ t('WORD.ITEMS') }}</span>
              </div>
            </li>
            <template
              v-for="(info, idx) in separatedMenuItems.regularItems"
              :key="`regular_${info.text}__${idx}`"
            >
              <right-panel-sub-menu-element
                v-if="!info.children?.length"
                :menu-info="info"
                :selected="subPanelInfo.selectedSubPanelMenu === info.value"
              />

              <li
                v-else
                class="mid-menu"
              >
                <div
                  v-if="shouldMidMenuTitleRendered(info.text)"
                  :class="['mid-menu__title', { 'mid-menu__title--empty': !info.text }]"
                >
                  <span>{{ info.text }}</span>
                </div>
                <ul>
                  <template
                    v-for="(childMenu, childIndex) in info.children"
                    :key="`${childMenu.value}_${childIndex}`"
                  >
                    <right-panel-sub-menu-element
                      :menu-info="childMenu"
                      :selected="subPanelInfo.selectedSubPanelMenu === childMenu.value"
                    />
                  </template>
                </ul>
              </li>
            </template>
          </template>
        </template>
      </ul>
    </nav>
  </transition>
</template>

<style lang="scss">
.panel-menu {
  display: flex;
  flex-direction: column;
  width: var(--gnb-menu-width-expanded);
  height: 100%;
  overflow: hidden;
  background-color: var(--right-panel-bg-color);
  color: var(--color-gray-00-00);

  &__list {
    max-height: calc(100% - var(--gnb-header-height));
    padding: 0 12px;
    overflow: auto;

    @media (max-width: 1600px) {
      padding: 0 8px;
    }

    &::-webkit-scrollbar {
      background-color: var(--right-panel-bg-color) !important;
    }

    &::-webkit-scrollbar-track {
      background-color: var(--right-panel-bg-color) !important;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--right-panel-bg-color) !important;
    }

    &:hover {
      background-color: var(--right-panel-bg-color);
      &::-webkit-scrollbar-thumb {
        background-color: var(--gnb-scrollbar-thumb-hover-color) !important;
      }
      &::-webkit-scrollbar-thumb:hover {
        background-color: var(--gnb-scrollbar-thumb-hover-active-color) !important;
      }
    }

    // LNB 패널 내부의 sub-menu-element는 width 100%로 동작
    .sub-menu-element {
      width: 100%;
    }
  }

  &__divider {
    width: 100%;
    height: 1px;
    margin: 8px 0;
    background-color: var(--gnb-menu-divider-color);
  }
}
.panel-menu-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
  justify-content: space-between;
  padding: 12px;

  &__row {
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    padding-left: 8px;
  }

  &__left {
    display: flex;
    flex: 1;
    flex-direction: row;
    gap: 8px;
    align-items: center;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;

    h2 {
      font-size: 14px;
      font-weight: 700;
      line-height: 1.2;
      color: var(--color-gray-00-00);
    }
  }

  .base-menu-header__slide-button {
    flex-shrink: 0;
  }

  i {
    background-color: var(--color-gray-00-00) !important;
  }
}
.panel-slide-in-enter-active,
.panel-slide-in-leave-active {
  transition: width $animate-gnb;
}
.panel-slide-in-enter-from,
.panel-slide-in-leave-to {
  width: 0;
}
.dashboard-item-empty {
  &__state {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    padding: 16px 12px;
    text-align: center;
  }

  &__message {
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: var(--color-gray-03-07);
    text-align: center;
  }

  &__button {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: center;
    padding: 9px 10px;
    cursor: pointer;
    border: 1px solid var(--color-gray-04-09);
    border-radius: 4px;
    background-color: transparent;
    font-size: 12px;
    font-weight: 500;
    line-height: normal;
    color: var(--color-gray-00-00);

    i::before {
      color: var(--color-gray-03-07);
    }

    &:hover {
      background-color: rgb(var(--color-gray-00-00-rgb), 0.06);
    }
    &:focus-visible {
      border-color: var(--color-blue-05);
      outline: none;
      box-shadow: 0 0 0 3px rgb(var(--color-gray-00-00-rgb), 0.12);
    }
    &:active {
      background-color: rgb(var(--color-gray-00-00-rgb), 0.12);
    }
  }
}

@include max-screen() {
  .menu-item {
    font-size: 11px;
  }
}
</style>
<style scoped lang="scss">
.mid-menu {
  &__title {
    padding: 16px 8px 4px;
    margin-bottom: var(--gnb-menu-margin-bottom);
  }
}
</style>
